// SetupWizard.tsx
import React, { useState } from "react";
import { apiService, UserProfile } from "../../services/apiService";
import abraLogo from '../../assets/abra-logo.png';  

const SignOutIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
       viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const TickCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="16 12 12 16 8 12"/>
    <line x1="12" y1="8" x2="12" y2="16"/>
  </svg>
);

interface SetupWizardProps {
  profile: UserProfile;
  onSetupComplete: () => void; 
}

export default function SetupWizard({ profile, onSetupComplete }: SetupWizardProps) {
  const handleSignOut = () => { window.location.href = '/'; };
  const [projectName, setProjectName] = useState(
    profile?.full_name?.split('@')[0] || 'My Project'
  );
  const [loading, setLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState('free');
  const [error, setError] = useState('');

  const handleSetupComplete = async () => {
    if (!projectName.trim()) {
      setError("Please enter a project name");
      return;
    }

    setLoading(true);
    try {
      await apiService.createProjectAndKey(profile.id, projectName);
      onSetupComplete();
    } catch (err) {
      setError("Failed to create project. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1 className="dashboard-title">
          <img src={abraLogo} alt="Abra Logo" className="logo" />
          Console
        </h1>

        <button className="button-signout" onClick={handleSignOut}>
         <SignOutIcon />
         <span>Sign Out</span>
        </button>
      </header>
      
      <div className="setup-container">
        <h1>Welcome to Abra</h1>
        <p className="setup-subtitle">
          Let's set up your first project to get started with the Abra platform.
        </p>
        
        {error && (
          <div style={{ 
            background: 'rgba(239, 68, 68, 0.1)', 
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '8px', 
            padding: '0.75rem 1rem', 
            marginBottom: '1.5rem', 
            color: 'var(--error)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>{error}</span>
          </div>
        )}
        
        <div className="dashboard-card">
          <div style={{ marginBottom: '2rem' }}>
            <label className="form-label">Project Name</label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              className="form-input"
              placeholder="My Awesome Project"
            />
            <p style={{ 
              marginTop: '0.5rem',
              fontSize: '0.85rem',
              color: 'var(--text-muted)'
            }}>
              This name is just for your reference. You can change it later.
            </p>
          </div>
          
          <div style={{ marginBottom: '2rem' }}>
            <label className="form-label">Plan</label>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
              gap: '1rem',
              marginBottom: '0.75rem'
            }}>
              <button
                onClick={() => setSelectedTier('free')}
                style={{
                  background: selectedTier === 'free' ? 'var(--primary-glow)' : 'transparent',
                  border: `1px solid ${selectedTier === 'free' ? 'var(--primary)' : 'var(--border-light)'}`,
                  borderRadius: '8px',
                  padding: '1rem',
                  color: selectedTier === 'free' ? 'var(--primary)' : 'var(--text)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center'
                }}>
                  <span style={{ fontWeight: '600' }}>Free</span>
                  {selectedTier === 'free' && (
                    <span style={{ color: 'var(--primary)' }}>
                      <TickCircleIcon />
                    </span>
                  )}
                </div>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  200K tokens/month
                </span>
              </button>
              
              <button
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px',
                  padding: '1rem',
                  color: 'var(--text-muted)',
                  cursor: 'not-allowed',
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                  opacity: 0.6
                }}
                disabled
              >
                <span style={{ fontWeight: '600' }}>Pro</span>
                <span style={{ fontSize: '0.8rem' }}>
                  Coming soon
                </span>
              </button>
              
              <button
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px',
                  padding: '1rem',
                  color: 'var(--text-muted)',
                  cursor: 'not-allowed',
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                  opacity: 0.6
                }}
                disabled
              >
                <span style={{ fontWeight: '600' }}>Enterprise</span>
                <span style={{ fontSize: '0.8rem' }}>
                  Contact sales
                </span>
              </button>
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              Paid plans coming soon with higher token limits and additional features.
            </p>
          </div>
          
          <button
            className="button-primary"
            onClick={handleSetupComplete}
            disabled={loading}
            style={{ width: '100%' }}
          >
            {loading ? (
              <div className="button-loading">
                <span className="spinner"></span>
                <span>Creating Project...</span>
              </div>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                <span>Create Project</span>
              </>
            )}
          </button>
        </div>
        
        <div style={{ 
          marginTop: '2rem', 
          textAlign: 'center', 
          fontSize: '0.85rem',
          color: 'var(--text-muted)'
        }}>
          By creating a project, you agree to our 
          <a href="/terms" style={{ color: 'var(--primary)', marginLeft: '0.25rem', marginRight: '0.25rem' }}>Terms of Service</a> 
          and 
          <a href="/privacy" style={{ color: 'var(--primary)', marginLeft: '0.25rem' }}>Privacy Policy</a>.
        </div>
      </div>
    </div>
  );
}